import { useQuery } from "@tanstack/react-query";
import { ResponsiveContainer, AreaChart, Area, YAxis, Tooltip } from "recharts";
import { fetchEU10Y } from "@/lib/api";
import { tooltipStyle, tooltipLabelStyle } from "@/lib/chartTheme";

export default function EU10YWidget() {
  const q = useQuery({
    queryKey: ["eu10y", 130],
    queryFn: () => fetchEU10Y(130),
    staleTime: 1000 * 60 * 30,
  });

  const latest = q.data?.latest;
  const points = q.data?.points || [];
  const chg1d = q.data?.change_1d;
  const chg1w = q.data?.change_1w;

  const sign = (v) =>
    v == null ? "text-zinc-500" : v > 0 ? "text-pos" : v < 0 ? "text-neg" : "text-zinc-500";
  const fmtBp = (v) =>
    v == null ? "—" : `${v >= 0 ? "+" : ""}${(v * 100).toFixed(1)} bp`;

  return (
    <div
      className="border border-zinc-800 bg-[#09090B] p-3 flex flex-col gap-2"
      data-testid="eu10y-widget"
    >
      <div className="flex items-baseline justify-between">
        <span className="text-[11px] uppercase tracking-widest text-zinc-500">
          EUR 10Y BENCHMARK · ECB
        </span>
        <span className="text-[11px] uppercase tracking-widest text-zinc-600">BUND-PROXY</span>
      </div>
      <div className="flex items-baseline gap-3">
        <span className="text-2xl font-semibold tabular-nums text-amber-term">
          {latest ? `${latest.yield.toFixed(3)}%` : q.isLoading ? "…" : "—"}
        </span>
        <span className={`text-xs tabular-nums ${sign(chg1d)}`}>{fmtBp(chg1d)} 1D</span>
        <span className={`text-xs tabular-nums ${sign(chg1w)}`}>{fmtBp(chg1w)} 1W</span>
      </div>
      <div className="h-[60px] -mx-1">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={points} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
            <defs>
              <linearGradient id="euYield" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.5} />
                <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
              </linearGradient>
            </defs>
            <YAxis hide domain={["dataMin", "dataMax"]} />
            <Tooltip
              contentStyle={tooltipStyle}
              labelStyle={tooltipLabelStyle}
              formatter={(v) => [`${v?.toFixed(3)}%`, "Yield"]}
            />
            <Area
              type="linear"
              dataKey="yield"
              stroke="hsl(var(--primary))"
              strokeWidth={1.5}
              fill="url(#euYield)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <div className="text-[11px] uppercase tracking-widest text-zinc-600">
        {latest?.date} · {points.length} obs · ~6M
      </div>
    </div>
  );
}
